import { faDatabase, faEdit, faPlus, faTrashAlt, faCogs, faCopy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import { FormDataControl, FormDataCheckbox } from '../../../gateway/components/FormFields'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import GatewayType from '../../../gateway/pages/GatewayType'
import TokenProfileService from '../../../gateway/service/TokenProfileService'
import { expandRowFunction, formatBooleanColumn } from '../../../gateway/utils/DatatableUtil'
import TestdataInDbService from './TestdataInDbService'

const InDbPolicy: GatewayType = () => {
  const { t } = useTranslation()
  const [inDbPolicies, setInDbPolicies] = useState([] as any[])
  const [inDbConfigs, setInDbConfigs] = useState([] as any[])
  const [tokenProfiles, setTokenProfiles] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      await TestdataInDbService.getAllPolicies(setInDbPolicies)
      await TestdataInDbService.getAllInDbConfigs(setInDbConfigs)
      await TokenProfileService.getConfigurableTokenProfiles(setTokenProfiles)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('testdata.indb.policy.name'),
        dataField: 'configName',
        sort: true,
        classes: 'position-relative',
      },
      {
        text: t('testdata.indb.policy.source'),
        dataField: 'srcDb',
        formatter: (cell: any, row: any) => {
          return `${row.srcDb} (${row.srcTable})`
        },
      },
      {
        text: t('testdata.indb.policy.destination'),
        dataField: 'destDb',
        formatter: (cell: any, row: any) => {
          if (!row.inTableReplace) {
            return `${row.destDb} (${row.destTable})`
          } else {
            return `${row.srcDb} (${row.srcTable})`
          }
        },
      },
      {
        text: t('testdata.indb.policy.emptyDestination'),
        dataField: 'emptyDestTable',
        allign: 'center',
        formatter: (cell: any) => formatBooleanColumn(cell),
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.view),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',

        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-decoration-none text-left'
                tooltip={t('edit')}
                onClick={() => editPolicyDialog(t, editDialogPropsSetter, setInDbPolicies, row, inDbConfigs)}>
                <FontAwesomeIcon icon={faEdit} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-decoration-none'
                tooltip={t('testdata.tokenizerConfig.addColumn')}
                onClick={() => addColumnDialog(t, editDialogPropsSetter, setInDbPolicies, tokenProfiles, row)}>
                <FontAwesomeIcon icon={faPlus} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-link text-decoration-none'
                tooltip={t('testdata.indb.policy.populateHeader')}
                shouldRender={!row.configs.length}
                onClick={() => populateTableDialog(t, confirmDialogPropsSetter, setInDbPolicies, row)}>
                <FontAwesomeIcon icon={faDatabase} />
              </ProtectedButton>
              <ProtectedButton
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('copy')}
                onClick={() => copyPolicyDialog(t, editDialogPropsSetter, setInDbPolicies, row)}>
                <FontAwesomeIcon icon={faCopy} />
              </ProtectedButton>
              <ProtectedButton
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('testdata.indb.policy.generateRows')}
                shouldRender={row.configs.length}
                onClick={() => generateCSV(t, editDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faCogs} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-danger text-link text-decoration-none text-right'
                tooltip={t('delete')}
                onClick={() => deletePolicyDialog(t, confirmDialogPropsSetter, setInDbPolicies, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, userPermissions, confirmDialogPropsSetter, editDialogPropsSetter, inDbConfigs, tokenProfiles])

  const expandRow = expandRowFunction(renderer, () => { }, t, confirmDialogPropsSetter, editDialogPropsSetter, setInDbPolicies, tokenProfiles)

  const defaultSorted = [
    {
      dataField: 'configName',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={inDbPolicies}
        columns={columns}
        caption={t('In-Database Policies')}
        helpLink={t('testdata.indb.policy.helpLink')}
        expandRow={expandRow}
        captionButtons={[
          {
            onClick: () => createPolicyDialog(t, editDialogPropsSetter, setInDbPolicies, inDbConfigs),
            text: t('new'),
            permissions: Authorization.adapter.edit,
            disabled: inDbConfigs.length === 0,
          },
        ]}
        searchPlaceholder='Search In-Database Policies'
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function createPolicyDialog(t: TFunction, editDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, inDbConfigs: any[]) {
  const methods = [t('testdata.indb.policy.inTableReplace'), t('testdata.indb.policy.tableToTable')]
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.policy.createHeader'),
    onSubmit: TestdataInDbService.createPolicy,
    onSubmitExtraArgs: [t, setInDbPolicies],
    data: (custom: string[]) => [
      {
        id: 'method',
        type: 'select',
        options: methods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('testdata.indb.policy.method'),
        required: true,
      },
      {
        id: 'inTableReplace',
        type: 'hidden',
        value: isInTableReplace(custom[0], t),
      },
      {
        id: 'configName',
        type: 'text',
        label: t('testdata.indb.policy.name'),
        required: true,
      },
      {
        groupLabel: t('testdata.indb.policy.skipError'),
        groupName: 'skipError',
        type: 'switch',
        checkboxes: [
          {
            id: 'skipError',
          },
        ],
      },
      ...getEmptyTableCheckBox(custom[0], t),
      ...getCreateFinalField(inDbConfigs, custom[0], t),
    ],
    initCustom: [t('testdata.indb.policy.tableToTable')],
    labelGridSize: 5,
  }

  editDialogPropsSetter(editDialogProps)
}

function isInTableReplace(custom: string, t: TFunction) {
  if (custom === t('testdata.indb.policy.inTableReplace')) {
    return 'true'
  } else {
    return 'false'
  }
}

function getCreateFinalField(inDbConfigs: any[], custom: string, t: TFunction): FormDataControl[] {
  if (custom === t('testdata.indb.policy.inTableReplace')) {
    return [
      {
        id: 'srcDb',
        type: 'select',
        options: inDbConfigs.map((config) => config.configName),
        value: inDbConfigs[0].configName,
        label: t('testdata.indb.policy.database'),
        required: true,
      },
      {
        id: 'srcTable',
        type: 'text',
        label: t('testdata.indb.policy.table'),
        required: true,
      },
    ]
  } else if (custom === t('testdata.indb.policy.tableToTable')) {
    return [
      {
        id: 'srcDb',
        type: 'select',
        options: inDbConfigs.map((config) => config.configName),
        value: inDbConfigs[0].configName,
        label: t('testdata.indb.policy.srcDb'),
        required: true,
      },
      {
        id: 'srcTable',
        type: 'text',
        label: t('testdata.indb.policy.srcTable'),
        required: true,
      },
      {
        id: 'destDb',
        type: 'select',
        options: inDbConfigs.map((config) => config.configName),
        label: t('testdata.indb.policy.destDb'),
        value: inDbConfigs[0].configName,
        required: true,
      },
      {
        id: 'destTable',
        type: 'text',
        label: t('testdata.indb.policy.destTable'),
        required: true,
      },
    ]
  }
  return []
}

function getEmptyTableCheckBox(custom: string, t: TFunction): FormDataCheckbox[] {
  if (custom === t('testdata.indb.policy.tableToTable')) {
    return [
      {
        groupLabel: t('testdata.indb.policy.emptyDestTable'),
        groupName: 'emptyDestTable',
        type: 'switch',
        checkboxes: [
          {
            id: 'emptyDestTable',
          },
        ],
      },
    ]
  }
  return []
}

function generateCSV(t: TFunction, editDialogPropsSetter: any, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.policy.generateRows'),
    onSubmit: TestdataInDbService.generateRows,
    onSubmitExtraArgs: [t],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'configName',
        value: row.configName,
        type: 'hidden',
        render: false,
      },
      {
        id: 'totalLines',
        type: 'number',
        label: t('testdata.indb.policy.totalLines'),
        placeholder: t('testdata.indb.policy.totalLines'),
        requiredMessage: t('invalidMessage', { label: t('testdata.indb.policy.totalLines') }),
        required: true,
      },
    ],
    labelGridSize: 2,
  }
  editDialogPropsSetter(editDialogProps)
}

function deletePolicyDialog(t: TFunction, confirmDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.indb.policy.deleteHeader'),
    body: t('testdata.indb.policy.deleteBody', { name: row.configName }),
    submitText: t('yes'),
    onSubmit: TestdataInDbService.deletePolicy,
    onSubmitExtraArgs: [row, t, setInDbPolicies],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function populateTableDialog(t: TFunction, confirmDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.indb.policy.populateHeader'),
    body: t('testdata.indb.policy.populateBody', { name: row.configName }),
    submitText: t('yes'),
    onSubmit: TestdataInDbService.populateTable,
    onSubmitExtraArgs: [row, t, setInDbPolicies],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editPolicyDialog(t: TFunction, editDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, row: any, inDbConfigs: any[]) {
  const methods = [t('testdata.indb.policy.inTableReplace'), t('testdata.indb.policy.tableToTable')]
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.policy.editHeader'),
    onSubmit: TestdataInDbService.updatePolicy,
    onSubmitExtraArgs: [t, setInDbPolicies],
    data: (custom: string[]) => [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'inTableReplace',
        type: 'hidden',
        value: row.inTableReplace,
      },
      {
        id: 'method',
        type: 'select',
        options: methods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('testdata.indb.policy.method'),
        required: true,
      },
      {
        id: 'configName',
        type: 'text',
        value: row.configName,
        label: t('testdata.indb.policy.name'),
        required: true,
      },
      {
        groupLabel: t('testdata.indb.policy.skipError'),
        groupName: 'skipError',
        type: 'switch',
        checkboxes: [
          {
            id: 'skipError',
          },
        ],
      },
      ...getEmptyTableCheckBoxForEdit(custom[0], row, t),
      ...getCreateFinalFieldForEdit(custom[0], row, inDbConfigs, t),
    ],
    initCustom: [getMethodValueForEdit(row, t)],
    labelGridSize: 5,
  }

  editDialogPropsSetter(editDialogProps)
}

function getMethodValueForEdit(row: any, t: TFunction) {
  if (row.inTableReplace === true) {
    return t('testdata.indb.policy.inTableReplace')
  } else {
    return t('testdata.indb.policy.tableToTable')
  }
}

function getCreateFinalFieldForEdit(custom: string, row: any, inDbConfigs: any[], t: TFunction): FormDataControl[] {
  if (custom === t('testdata.indb.policy.inTableReplace')) {
    return [
      {
        id: 'emptyDestTable',
        type: 'hidden',
        value: 'false',
      },
      {
        id: 'srcDb',
        type: 'select',
        options: inDbConfigs.map((config) => config.configName),
        value: row.srcDb,
        label: t('testdata.indb.policy.database'),
        required: true,
      },
      {
        id: 'srcTable',
        type: 'text',
        value: row.srcTable,
        label: t('testdata.indb.policy.table'),
        required: true,
      },
    ]
  } else if (custom === t('testdata.indb.policy.tableToTable')) {
    return [
      {
        id: 'srcDb',
        type: 'select',
        options: inDbConfigs.map((config) => config.configName),
        value: row.srcDb,
        label: t('testdata.indb.policy.srcDb'),
        required: true,
      },
      {
        id: 'srcTable',
        type: 'text',
        value: row.srcTable,
        label: t('testdata.indb.policy.srcTable'),
        required: true,
      },
      {
        id: 'destDb',
        type: 'select',
        options: inDbConfigs.map((config) => config.configName),
        value: inDbConfigs[0].configName,
        label: t('testdata.indb.policy.destDb'),
        required: true,
      },
      {
        id: 'destTable',
        type: 'text',
        value: row.destTable,
        label: t('testdata.indb.policy.destTable'),
        required: true,
      },
    ]
  }
  return []
}

function getEmptyTableCheckBoxForEdit(custom: any, row: any, t: TFunction): FormDataCheckbox[] {
  if (custom === t('testdata.indb.policy.tableToTable')) {
    return [
      {
        groupLabel: t('testdata.indb.policy.emptyDestTable'),
        groupName: 'emptyDestTable',
        type: 'switch',
        checkboxes: [
          {
            id: 'emptyDestTable',
            value: row.emptyDestTable,
          },
        ],
      },
    ]
  } else {
    return []
  }
}

function copyPolicyDialog(t: TFunction, editDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.policy.copyHeader'),
    onSubmit: TestdataInDbService.copyPolicy,
    onSubmitExtraArgs: [t, setInDbPolicies],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'configName',
        type: 'text',
        label: t('testdata.indb.policy.name'),
        placeholder: t('testdata.indb.policy.name'),
        requiredMessage: t('testdata.indb.policy.invalidMessage'),
        required: true,
      },
    ],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function addColumnDialog(t: TFunction, editDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, tokenProfiles: any[], row: any) {
  const encryptionMethods = [
    t('testdata.tokenizerConfig.methods.retainValues'),
    t('testdata.tokenizerConfig.methods.encryption'),
    t('testdata.tokenizerConfig.methods.tokenization'),
    t('testdata.tokenizerConfig.methods.staticReplacement'),
  ]
  const dataTypes = [
    'BFILE',
    'BIGINT',
    'BINARY',
    'BLOB',
    'CHAR () FOR BIT DATA',
    'CHAR',
    'CLOB',
    'DATE',
    'DECIMAL',
    'DOUBLE',
    'FLOAT',
    'INT',
    'INTEGER',
    'LONG RAW',
    'LONGVARBINARY',
    'LONGVARCHAR',
    'NCHAR',
    'NCLOB',
    'NUMBER',
    'NUMERIC',
    'NVARCHAR2',
    'RAW',
    'REAL',
    'SMALLINT',
    'TEXT',
    'TIME',
    'TIMESTAMP',
    'TINYINT',
    'VARBINARY',
    'VARCHAR () FOR BIT DATA',
    'VARCHAR',
    'VARCHAR2',
  ]
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.tokenizerConfig.addColumn'),
    onSubmit: TestdataInDbService.addTokenizerConfig,
    onSubmitExtraArgs: [t, setInDbPolicies],
    data: (custom: string[]) => [
      {
        id: 'configId',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'method',
        type: 'select',
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('testdata.tokenizerConfig.method'),
        required: true,
      },
      {
        id: 'columnName',
        type: 'text',
        label: t('testdata.tokenizerConfig.name'),
        required: true,
      },
      {
        id: 'dataType',
        type: 'select',
        options: dataTypes,
        value: 'VARCHAR',
        label: t('testdata.tokenizerConfig.dataType'),
        required: true,
      },
      ...getFinalField(tokenProfiles, custom[0], t),
    ],
    initCustom: [t('testdata.tokenizerConfig.methods.retainValues')],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function getFinalField(tokenProfiles: any[], custom: string, t: TFunction): FormDataControl[] {
  if (custom === t('testdata.tokenizerConfig.methods.staticReplacement')) {
    return [
      {
        id: 'property',
        type: 'text',
        label: t('testdata.tokenizerConfig.replacementText'),
        required: true,
      },
    ]
  } else if (custom === t('testdata.tokenizerConfig.methods.tokenization')) {
    return [
      {
        id: 'tokenProfile',
        type: 'select',
        options: tokenProfiles.map((profile) => profile.name),
        value: tokenProfiles[0].name,
        label: t('testdata.tokenizerConfig.tokenProfile'),
        required: true,
      },
    ]
  }
  return []
}

function renderer(row: any, rowIndex: any, ...args: any[]) {
  const data: any[] = row.configs
  const t: TFunction = args[0]
  const confirmDialogPropsSetter = args[1]
  const editDialogPropsSetter = args[2]
  const setInDbPolicies = args[3]
  const tokenProfiles = args[4]

  const columns: any[] = [
    {
      text: t('testdata.tokenizerConfig.name'),
      dataField: 'columnName',
      sort: true,
    },
    {
      text: t('testdata.tokenizerConfig.mode'),
      dataField: 'method',
      formatter: (cell: any, row: any) => {
        if (cell === t('testdata.tokenizerConfig.methods.staticReplacement')) {
          return `${cell} (${row.property})`
        } else if (cell === t('testdata.tokenizerConfig.methods.tokenization')) {
          return `${cell} (${row.tokenProfile})`
        } else {
          return cell
        }
      },
    },
    {
      dataField: 'df1',
      isDummyField: true,
      text: t('action'),
      headerAlign: 'center',
      formatter: (cellData: any, row: any) => {
        return (
          <>
            <ProtectedButton
              permissions={Authorization.adapter.edit}
              variant='link'
              className='text-link text-decoration-none'
              tooltip={t('edit')}
              onClick={() => editColumnDialog(t, editDialogPropsSetter, setInDbPolicies, tokenProfiles, row)}>
              <FontAwesomeIcon icon={faEdit} />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.adapter.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteColumnDialog(t, confirmDialogPropsSetter, setInDbPolicies, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          </>
        )
      },
    },
  ]

  if (!data.length) {
    return <React.Fragment key={row.id} />
  }
  return (
    <React.Fragment key={row.id}>
      <DataTable
        data={data}
        columns={columns}
        searchable={false}
        striped={false}
        hover={false}
        condensed={true}
        noDataIndication={() => <span className='text-center'>{t('testdata.tokenizerConfig.noConfigFound')}</span>}
      />
    </React.Fragment>
  )
}

function deleteColumnDialog(t: TFunction, confirmDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.tokenizerConfig.deleteHeader'),
    body: t('testdata.tokenizerConfig.deleteBody', { name: row.columnName }),
    submitText: t('yes'),
    onSubmit: TestdataInDbService.deleteTokenizerConfig,
    onSubmitExtraArgs: [row, t, setInDbPolicies],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editColumnDialog(t: TFunction, editDialogPropsSetter: any, setInDbPolicies: React.Dispatch<any>, tokenProfiles: any[], row: any) {
  const encryptionMethods = [
    t('testdata.tokenizerConfig.methods.retainValues'),
    t('testdata.tokenizerConfig.methods.encryption'),
    t('testdata.tokenizerConfig.methods.tokenization'),
    t('testdata.tokenizerConfig.methods.staticReplacement'),
  ]
  const dataTypes = [
    'BFILE',
    'BIGINT',
    'BINARY',
    'BLOB',
    'CHAR () FOR BIT DATA',
    'CHAR',
    'CLOB',
    'DATE',
    'DECIMAL',
    'DOUBLE',
    'FLOAT',
    'INT',
    'INTEGER',
    'LONG RAW',
    'LONGVARBINARY',
    'LONGVARCHAR',
    'NCHAR',
    'NCLOB',
    'NUMBER',
    'NUMERIC',
    'NVARCHAR2',
    'RAW',
    'REAL',
    'SMALLINT',
    'TEXT',
    'TIME',
    'TIMESTAMP',
    'TINYINT',
    'VARBINARY',
    'VARCHAR () FOR BIT DATA',
    'VARCHAR',
    'VARCHAR2',
  ]
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.tokenizerConfig.editHeader'),
    onSubmit: TestdataInDbService.updateTokenizerConfig,
    onSubmitExtraArgs: [t, setInDbPolicies],
    data: (custom: string[]) => [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'configId',
        type: 'hidden',
        value: row.configId,
      },
      {
        id: 'method',
        type: 'select',
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('testdata.tokenizerConfig.method'),
        required: true,
      },
      {
        id: 'columnName',
        type: 'text',
        value: row.columnName,
        label: t('testdata.tokenizerConfig.name'),
        required: true,
      },
      {
        id: 'dataType',
        type: 'select',
        options: dataTypes,
        value: row.dataType,
        label: t('testdata.tokenizerConfig.dataType'),
        required: true,
      },
      ...getEditFinalField(row, tokenProfiles, custom[0], t),
    ],
    initCustom: [row.method],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function getEditFinalField(row: any, tokenProfiles: any[], custom: string, t: TFunction): FormDataControl[] {
  if (custom === t('testdata.tokenizerConfig.methods.staticReplacement')) {
    return [
      {
        id: 'property',
        type: 'text',
        value: row.property,
        label: t('testdata.tokenizerConfig.replacementText'),
        required: true,
      },
    ]
  } else if (custom === t('testdata.tokenizerConfig.methods.tokenization')) {
    return [
      {
        id: 'tokenProfile',
        type: 'select',
        options: tokenProfiles.map((profile) => profile.name),
        value: row.tokenProfile,
        label: t('testdata.tokenizerConfig.tokenProfile'),
        required: true,
      },
    ]
  }
  return []
}

export default InDbPolicy
